import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Gift, Coffee } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import Navbar from '@/components/landing/Navbar'
import Reveal from '@/components/Reveal'
import { useSEO } from '@/hooks/useSEO'
import { usePrerenderReady } from '@/hooks/usePrerenderReady'
import { useLanguage } from '@/context/LanguageContext'
import FavoriteBoardButton from '@/components/wishlist/FavoriteBoardButton'
import { boardSlug } from '@/lib/wishlist'

interface CommunityItem {
  id: string
  title: string
  image_url: string | null
  image_urls: string[] | null
}

interface CommunityBoard {
  id: string
  title: string
  username: string
  display_name: string | null
  avatar_url: string | null
  cover_url: string | null
  profile_cover_url: string | null
  kind: string
  items: CommunityItem[]
}

interface FavoriteState {
  count: number
  favorited: boolean
}

const i18n = {
  it: {
    badge: 'Community',
    title: 'Le bacheche della community',
    subtitle: 'Sfoglia le wishlist pubbliche degli utenti di Make me a Gift e trova l\u2019ispirazione per il regalo perfetto.',
    empty: 'Ancora nessuna bacheca pubblica da mostrare. Sarai il primo?',
    error: 'Non è stato possibile caricare le bacheche. Riprova più tardi.',
    visit: 'Visita la bacheca',
    wishes: 'Desideri',
    by: 'di',
    cta: 'Crea la tua wishlist',
    advice: 'Consigli',
    tabAll: 'Tutte',
    tabFavorites: 'Preferite ♥',
    favoritesEmpty: 'Non hai ancora salvato bacheche tra i preferiti.',
  },
  en: {
    badge: 'Community',
    title: 'Community boards',
    subtitle: 'Browse public wishlists created by Make me a Gift users and get inspired for the perfect gift.',
    empty: 'No public boards to show yet. Be the first!',
    error: 'We could not load the boards. Please try again later.',
    visit: 'Visit the board',
    wishes: 'Wishes',
    by: 'by',
    cta: 'Create your wishlist',
    advice: 'Advice',
    tabAll: 'All',
    tabFavorites: 'Favorites ♥',
    favoritesEmpty: "You haven't saved any favorites yet.",
  },
}

function itemImage(item: CommunityItem): string | null {
  return item.image_url || (item.image_urls && item.image_urls.length > 0 ? item.image_urls[0] : null)
}

interface BoardCardProps {
  board: CommunityBoard
  favorite?: FavoriteState
  t: (typeof i18n)['it']
}

function BoardCard({ board, favorite, t }: BoardCardProps) {
  const boardPath = `/w/${board.username}/${boardSlug({ title: board.title, id: board.id })}`
  const itemsWithImage = board.items.filter(itemImage)
  const pool = itemsWithImage.length >= 4 ? itemsWithImage : board.items
  const randomItems = [...pool].sort(() => Math.random() - 0.5).slice(0, 8)
  return (
    <motion.article
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-2xl bg-neutral-100/60 p-5 sm:p-8"
    >
      {/* Row 1: identity + favorite + CTA button */}
      <div className="flex items-start justify-between gap-4 pt-0 sm:pt-4">
        <div className="flex items-start gap-5">
          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full bg-neutral-100 sm:h-20 sm:w-20">
            {board.avatar_url ? (
              <img src={board.avatar_url} alt={board.display_name || board.username} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-lg font-medium text-neutral-400" style={{ fontFamily: "'Playfair Display', serif" }}>
                {(board.display_name || board.username).charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div>
            <Link to={boardPath} className="group block">
              <h2
                className="text-3xl sm:text-4xl md:text-[2.75rem] font-medium leading-[1.1] tracking-tight text-neutral-900 transition-opacity group-hover:opacity-70"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {board.title}
              </h2>
              {board.kind === 'guide' && (
                <span className="mt-6 inline-flex items-center gap-1 rounded-full bg-pink-100 px-2.5 py-0.5 text-xs font-semibold text-pink-700">
                  {t.advice}
                </span>
              )}
            </Link>
            <p className="mt-2 text-sm text-neutral-500">
              {t.by}{' '}
              <Link to={boardPath} className="text-neutral-700 underline-offset-4 hover:underline">
                {board.display_name || `@${board.username}`}
              </Link>
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <FavoriteBoardButton
            wishlistId={board.id}
            initialCount={favorite?.count ?? 0}
            initiallyFavorited={favorite?.favorited ?? false}
            compact
          />
          <Link
            to={boardPath}
            className="hidden items-center gap-2 rounded-full bg-neutral-900 px-6 py-3 text-sm font-medium text-white transition-opacity hover:opacity-80 sm:inline-flex"
          >
            {t.visit} →
          </Link>
        </div>
      </div>

      {/* Row 2: 2×4 random item grid */}
      <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6">
        {randomItems.map((item) => {
          const src = itemImage(item)
          return (
            <Link
              key={item.id}
              to={boardPath}
              className="group block overflow-hidden bg-neutral-50"
            >
              {src ? (
                <img
                  src={src}
                  alt={item.title}
                  loading="lazy"
                  className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              ) : (
                <div className="flex aspect-square w-full items-center justify-center bg-neutral-100 p-4 text-center">
                  <span className="text-xs uppercase tracking-[0.14em] text-neutral-400">{item.title}</span>
                </div>
              )}
            </Link>
          )
        })}
      </div>
    </motion.article>
  )
}

type Tab = 'all' | 'favorites'

interface FavoriteBoardRow {
  wishlist_id: string
  title: string
  kind: string
  cover_url: string | null
  username: string
  updated_at: string
}

export default function CommunityPage() {
  const { language, t: tf } = useLanguage()
  const t = i18n[language]
  const [boards, setBoards] = useState<CommunityBoard[]>([])
  const [favorites, setFavorites] = useState<Record<string, FavoriteState>>({})
  const [favoriteBoards, setFavoriteBoards] = useState<CommunityBoard[] | null>(null)
  const [favoritesLoading, setFavoritesLoading] = useState(false)
  const [signedIn, setSignedIn] = useState(false)
  const [tab, setTab] = useState<Tab>('all')
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  useSEO({
    title: 'Community — Make me a Gift',
    description: t.subtitle,
  })
  usePrerenderReady()

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setSignedIn(!!data.session)
    })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false

    async function load(retry = true) {
      try {
        const { data, error } = await supabase
          .from('wishlists')
          .select('id, title, cover_url, user_id, created_at, kind')
          .eq('is_private', false)
          .order('created_at', { ascending: false })
          .limit(48)

        if (cancelled) return
        if (error) {
          if (retry) {
            setTimeout(() => { if (!cancelled) load(false) }, 1500)
            return
          }
          setFailed(true)
          setLoading(false)
          return
        }

        const wishlistIds = (data ?? []).map((row: any) => row.id)
        const itemsByWishlist = new Map<string, CommunityItem[]>()
        if (wishlistIds.length > 0) {
          const { data: itemRows, error: itemsError } = await supabase
            .from('wishlist_items')
            .select('id, wishlist_id, title, image_url, image_urls')
            .in('wishlist_id', wishlistIds)
            .eq('is_hidden', false)
            .order('position', { ascending: true })
          if (itemsError) throw itemsError
          for (const item of (itemRows ?? []) as any[]) {
            if (!item.wishlist_id) continue
            const list = itemsByWishlist.get(item.wishlist_id) ?? []
            list.push({
              id: item.id,
              title: item.title,
              image_url: item.image_url,
              image_urls: item.image_urls,
            })
            itemsByWishlist.set(item.wishlist_id, list)
          }
        }

        // Favorite counts + viewer state for each board (works for anon visitors)
        const favoritesMap: Record<string, FavoriteState> = {}
        if (wishlistIds.length > 0) {
          try {
            const { data: favRows, error: favError } = await supabase.rpc('get_board_favorites', {
              _wishlist_ids: wishlistIds,
            })
            if (favError) throw favError
            for (const row of (favRows ?? []) as any[]) {
              favoritesMap[row.wishlist_id] = { count: row.favorite_count, favorited: row.favorited }
            }
          } catch (favCause) {
            console.error('Unable to load board favorites', favCause)
          }
        }

        const userIds = Array.from(new Set((data ?? []).map((row: any) => row.user_id).filter(Boolean)))
        let profilesById = new Map<string, any>()
        if (userIds.length > 0) {
          const { data: profileRows, error: profilesError } = await supabase
            .from('profiles')
            .select('id, username, display_name, avatar_url, wishlist_cover_url')
            .in('id', userIds)
          if (profilesError) throw profilesError
          profilesById = new Map((profileRows ?? []).map((p: any) => [p.id, p]))
        }

        const mapped: CommunityBoard[] = (data ?? [])
          .filter((row: any) =>
            (itemsByWishlist.get(row.id)?.length ?? 0) >= 8 &&
            profilesById.get(row.user_id)?.username
          )
          .map((row: any) => {
            const profile = profilesById.get(row.user_id)
            return {
              id: row.id,
              title: row.title || t.wishes,
              username: profile.username,
              display_name: profile.display_name,
              avatar_url: profile.avatar_url,
              cover_url: row.cover_url,
              profile_cover_url: profile.wishlist_cover_url,
              kind: row.kind ?? 'wishlist',
              items: itemsByWishlist.get(row.id) ?? [],
            }
          })

        if (!cancelled) {
          setBoards(mapped)
          setFavorites(favoritesMap)
          setFailed(false)
          setLoading(false)
        }
      } catch {
        if (!cancelled) {
          if (retry) {
            setTimeout(() => { if (!cancelled) load(false) }, 1500)
          } else {
            setFailed(true)
            setLoading(false)
          }
        }
      }
    }

    load()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (tab !== 'favorites' || !signedIn || favoriteBoards || favoritesLoading) return
    let cancelled = false
    setFavoritesLoading(true)
    ;(async () => {
      try {
        const { data, error } = await supabase.rpc('list_favorite_boards')
        if (error) throw error
        if (!cancelled) {
          const rows = (data ?? []) as unknown as FavoriteBoardRow[]
          setFavoriteBoards(
            rows.map((row) => ({
              id: row.wishlist_id,
              title: row.title || t.wishes,
              username: row.username,
              display_name: row.username,
              avatar_url: null,
              cover_url: row.cover_url,
              profile_cover_url: null,
              kind: row.kind ?? 'wishlist',
              items: [],
            }))
          )
        }
      } catch (cause) {
        console.error('Unable to load favorite boards', cause)
        if (!cancelled) setFavoriteBoards([])
      } finally {
        if (!cancelled) setFavoritesLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [tab, signedIn, favoriteBoards, favoritesLoading, t.wishes])

  const visibleBoards = tab === 'favorites' ? (favoriteBoards ?? []) : boards
  const showFavoritesLoading = tab === 'favorites' && favoritesLoading

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 sm:px-6 md:px-10 pt-24 sm:pt-32 pb-20">
        <Reveal>
          <div className="mb-6 sm:mb-8 w-[95%]">
            <h1 className="text-balance text-4xl sm:text-6xl md:text-7xl font-medium tracking-tight leading-[1.05] text-neutral-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t.title}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-neutral-500 leading-relaxed">
              {t.subtitle}
            </p>
          </div>
        </Reveal>

        {/* Tab / filter row */}
        <div className="mb-10 flex items-center gap-2 sm:mb-12">
          <button
            type="button"
            onClick={() => setTab('all')}
            aria-pressed={tab === 'all'}
            className={`inline-flex min-h-[40px] items-center rounded-full px-4 py-2 text-sm font-medium transition ${tab === 'all' ? 'bg-neutral-900 text-white' : 'border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'}`}
          >
            {t.tabAll}
          </button>
          {signedIn && (
            <button
              type="button"
              onClick={() => setTab('favorites')}
              aria-pressed={tab === 'favorites'}
              className={`inline-flex min-h-[40px] items-center rounded-full px-4 py-2 text-sm font-medium transition ${tab === 'favorites' ? 'bg-neutral-900 text-white' : 'border border-pink-500 bg-white text-pink-600 hover:bg-pink-50'}`}
            >
              {t.tabFavorites}
            </button>
          )}
        </div>

        {(loading || showFavoritesLoading) && (
          <div className="space-y-10">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                <div className="h-72 animate-pulse rounded-none bg-neutral-100" />
                <div className="space-y-6">
                  <div className="h-10 w-3/4 animate-pulse bg-neutral-100" />
                  <div className="grid grid-cols-3 gap-4">
                    {Array.from({ length: 3 }).map((_, j) => (
                      <div key={j} className="h-40 animate-pulse bg-neutral-100" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !showFavoritesLoading && !failed && visibleBoards.length === 0 && (
          <div className="border border-neutral-200 bg-white p-12 text-center">
            <p className="text-neutral-600">
              {tab === 'favorites' ? t.favoritesEmpty : t.empty}
            </p>
          </div>
        )}

        {!loading && !showFavoritesLoading && failed && tab === 'all' && (
          <div className="border border-neutral-200 bg-white p-10 text-center text-neutral-600">
            {t.error}
          </div>
        )}

        {!loading && !showFavoritesLoading && !failed && visibleBoards.length > 0 && (
          <div className="space-y-16 sm:space-y-20">
            {visibleBoards.map((board) => (
              <BoardCard key={board.id} board={board} favorite={favorites[board.id]} t={t} />
            ))}
          </div>
        )}

        {!loading && !failed && tab === 'all' && (
          <Reveal>
            <div className="mt-10 flex justify-center sm:mt-14">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 rounded-full bg-neutral-900 px-8 py-4 text-sm font-medium text-white transition-opacity hover:opacity-80"
              >
                {t.cta} →
              </Link>
            </div>
          </Reveal>
        )}
      </main>

      <footer className="bg-neutral-900 text-white"><div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14"><div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 items-start sm:items-start text-center sm:text-left">
        <div className="flex flex-col gap-3 items-center sm:items-start"><Link to="/" className="flex items-center gap-2"><span className="w-9 h-9 rounded-lg bg-white text-neutral-900 flex items-center justify-center"><Gift className="w-4 h-4" /></span><span className="text-lg font-semibold font-logo">Make me a Gift</span></Link><p className="text-sm text-neutral-400 max-w-xs leading-relaxed">{tf('footer.description')}</p><div className="flex items-center gap-4 text-sm pt-1"><Link to="/cookie-policy" className="text-neutral-300 hover:text-white transition-colors">Cookie</Link><span className="text-neutral-600">·</span><Link to="/privacy" className="text-neutral-300 hover:text-white transition-colors">Privacy</Link><span className="text-neutral-600">·</span><Link to="/terms" className="text-neutral-300 hover:text-white transition-colors">{tf('footer.terms')}</Link><span className="text-neutral-600">·</span><Link to="/about" className="text-neutral-300 hover:text-white transition-colors">About</Link></div></div>
        <div className="flex flex-col gap-3 items-center sm:items-start"><p className="text-sm font-semibold text-white">{tf('footer.support.heading')}</p><p className="text-sm text-neutral-400 leading-relaxed max-w-xs">{tf('footer.support.text')}</p><a href="https://ko-fi.com/aurarinoa" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#FF5E5B] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#e54e4b] w-fit"><Coffee className="h-4 w-4" />{tf('footer.support.button')}</a></div>
        <div className="flex flex-col gap-3 items-center sm:items-start lg:items-end"><div className="flex items-center"><p className="text-sm font-semibold text-white mr-1">{tf('footer.madeWith')}</p><a href="https://coderblock.ai/" target="_blank" rel="noreferrer" className="transition-opacity hover:opacity-100 inline-flex items-center"><img src="/images/coderblock.svg" alt="Coderblock" className="max-h-8 w-auto brightness-0 invert opacity-70" /></a></div><p className="text-sm text-neutral-400 leading-relaxed max-w-xs lg:text-right">{tf('footer.idea')} <strong>Aura Nuccio</strong></p><div className="flex items-center gap-4 text-sm pt-1"><Link to="/login" className="text-neutral-300 hover:text-white transition-colors">{tf('footer.login')}</Link><span className="text-neutral-600">·</span><Link to="/register" className="text-neutral-300 hover:text-white transition-colors">{tf('footer.register')}</Link></div></div>
      </div><div className="mt-8 pt-6 border-t border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left"><p className="text-xs text-neutral-500">© {new Date().getFullYear()} Make me a Gift. {tf('footer.rights')}</p></div></div></footer>
    </div>
  )
}
